import { Button } from '../Button';
import type { TypeOfOptional } from '@repo/types/Types';
import { z } from 'zod';
import {
  isZodQueryReturn,
  type UseZodFormOrQueryReturn,
  type UseZodQueryReturn,
} from '@repo/hooks/useZodQuery';
import { useCallback } from 'react';
import { useDeepCompareEffect } from 'react-use';
import type { UseZodFormReturn } from '@repo/hooks/useZodForm';
import { hackType } from '@repo/util/hackType';
import type { Mercury } from '@repo/types/Models';

function encodePaging(paging: { before: string | undefined } | { after: string | undefined }) {
  const { type, page } =
    'after' in paging ? { type: 'A', page: paging.after } : { type: 'B', page: paging.before };
  if (!page) {
    return undefined;
  }

  return btoa(`${type}${page}`);
}

export function decodePaging(input: string | undefined, pageSize: number) {
  if (!input) {
    return {
      first: pageSize,
    };
  }
  try {
    const str = atob(input);
    const type = str[0];
    const page = str.substring(1);
    if (type === 'A') {
      return {
        first: pageSize,
        after: page,
      };
    } else if (type === 'B') {
      return {
        last: pageSize,
        before: page,
      };
    }
    // eslint-disable-next-line no-empty
  } catch (error) {}
  return {
    first: pageSize,
  };
}

export const PAGE_SCHEMA = z.object({
  page: z.string().optional(),
});

export type PagingSchema = typeof PAGE_SCHEMA;

export interface PaginationParams<T extends PagingSchema> {
  filter: UseZodFormReturn<T> | UseZodQueryReturn<T>;
  pageInfo?: Mercury.GQL.PageInfo;
}

export const Pagination = <T extends PagingSchema = PagingSchema>({
  filter,
  pageInfo,
}: PaginationParams<T>) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { page: _page, ...rest } = isZodQueryReturn(filter)
    ? filter.queryValues
    : filter.getValues();

  const setPaging = useCallback(
    (page: string | undefined) => {
      hackType<UseZodFormOrQueryReturn<PagingSchema>>(filter);
      if (isZodQueryReturn(filter)) {
        filter.setPartial({ page } as Partial<TypeOfOptional<T>>);
      } else {
        filter.setValue('page', page);
      }
    },
    [filter]
  );

  useDeepCompareEffect(() => {
    setPaging('');
  }, [filter.setValue, rest]);

  let prevPageButton = <></>;
  let nextPageButton = <></>;

  const loadMore = useCallback(() => {
    setPaging(
      encodePaging({
        after: pageInfo?.endCursor,
      })
    );
  }, [setPaging, pageInfo?.endCursor]);

  const loadPrevious = useCallback(() => {
    setPaging(pageInfo?.startCursor ? encodePaging({ before: pageInfo?.startCursor }) : undefined);
  }, [setPaging, pageInfo?.startCursor]);

  if (pageInfo && pageInfo.hasPreviousPage) {
    prevPageButton = (
      <Button onClick={loadPrevious} className="min-w-36">
        <span className="-ml-2 -mr-1 text-blue-100 material-symbols-outlined">chevron_left</span>
        <span> previous</span>
      </Button>
    );
  }

  if (pageInfo && pageInfo.hasNextPage) {
    nextPageButton = (
      <Button onClick={loadMore} className="min-w-36">
        <span>next</span>
        <span className="-ml-1 -mr-2 text-blue-100 material-symbols-outlined">chevron_right</span>
      </Button>
    );
  }

  return (
    <div className="flex items-center justify-center w-full gap-10 py-5 mx-auto min-h-[74px]">
      <div>{prevPageButton}</div>
      <div>{nextPageButton}</div>
    </div>
  );
};
